<template>
  <div id="upload-withholding-document-page">
    <v-container>
      <v-alert
        v-model="alert"
        type="success"
        color="primary"
        transition="slide-y-transition"
        dismissible
      >
        คุณได้ทำการอัปโหลดเอกสารหัก ณ ที่จ่ายสำเร็จแล้ว
      </v-alert>

      <v-card class="pa-10 mb-8">
        <div class="_fs-36">
          แนบเอกสารภาษีหัก ณ ที่จ่าย
        </div>
        <div>แนบหลักฐานเอกสารภาษีหัก ณ ที่จ่ายของท่าน</div>

        <v-divider class="mt-4 mb-8" />

        <validation-observer ref="uploadWithholdingDocumentForm">
          <!-- Top Section - Order Information -->
          <div class="mb-8">
            <validation-provider
              v-slot="{ errors }"
              ref="selectOrderId"
              rules="required"
              name="เลขที่เอกสาร"
            >
              <v-select
                v-model="selectOrderId"
                label="เลขที่เอกสาร"
                filled
                rounded
                :items="mapOrders"
                :error-messages="errors"
                @change="selectOrder"
              />
            </validation-provider>

            <base-text-field
              label="ราคาสุทธิสินค้าที่เสียภาษี (บาท) / Pre-VAT Amount"
              :value="formatNumber(order.preVatAmount)"
              readonly
            />

            <base-text-field
              label="ภาษีถูกหัก ณ ที่จ่าย (บาท) / Withholding tax"
              :value="formatNumber(order.withholdingTaxAmount)"
              readonly
            />
          </div>

          <v-divider class="my-8" />

          <!-- Payment Method Section (Readonly) -->
          <div class="_fs-24 mb-4">
            วิธีการชำระเงิน
          </div>

          <v-radio-group
            :value="paymentMethod"
            row
            readonly
            disabled
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-radio
                  value="qrCode"
                >
                  <template v-slot:label>
                    <div>
                      <v-icon>mdi-qrcode</v-icon>
                      QR Code
                    </div>
                  </template>
                </v-radio>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-radio
                  value="transfer"
                >
                  <template v-slot:label>
                    <div>
                      <v-icon>mdi-cash-multiple</v-icon>
                      โอน/ชำระผ่านบัญชีธนาคาร
                    </div>
                  </template>
                </v-radio>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-radio
                  value="byCreditCard"
                >
                  <template v-slot:label>
                    <div>
                      <v-icon>mdi-credit-card-outline</v-icon>
                      บัตรเครดิต/บัตรเดบิต
                    </div>
                  </template>
                </v-radio>
              </v-col>
            </v-row>
          </v-radio-group>

          <v-divider class="my-8" />

          <!-- File Upload Display -->
          <template v-if="hasFiles">
            <v-row
              class="mb-4"
              justify="center">
              <v-col
                v-for="(item, index) in listWithholdingDocumentSrc"
                :key="index"
                cols="auto"
                class="ml-2"
                style="position: relative;"
              >
                <img
                  v-if="isImageDocument(item.file)"
                  :src="item.url"
                  alt="Upload file"
                  width="100%"
                  height="200px"
                >
                <iframe
                  v-else
                  :src="item.url"
                  width="100%"
                  height="500px">
                </iframe>
                <v-icon
                  color="black"
                  style="position: absolute; top: 15px; right: 15px;"
                  size="24"
                  @click="removeWithholdingDocumentFile(index)"
                >
                  mdi-close-circle
                </v-icon>
              </v-col>
            </v-row>
          </template>

          <!-- File Upload Input -->
          <validation-provider
            v-slot="{ errors }"
            ref="withholdingDocument"
            rules="fileUpload|maximumSizeFile:10"
            name="เอกสารหัก ณ ที่จ่าย">
            <base-file-input
              label="เอกสารหัก ณ ที่จ่าย"
              multiple
              accept="image/jpg, image/jpeg, image/png, application/pdf"
              prepend-icon="mdi-camera"
              :messages="`กรุณาอัพโหลดเอกสารหัก ณ ที่จ่าย ที่มีนามสกุลไฟล์เป็น jpg, png, pdf และมีขนาดไฟล์ไม่เกิน 10mb`"
              :error-messages="errors"
              :value="withholdingDocumentFile"
              :clearable="false"
              @change="uploadWithholdingDocumentFile"
            />
          </validation-provider>

          <v-row
            class="mt-8"
            justify="center">
            <v-btn
              color="primary"
              rounded
              min-width="150"
              :loading="isSubmitting"
              :disabled="!hasFiles || !selectOrderId"
              @click="submit">
              ยืนยัน
            </v-btn>
          </v-row>
        </validation-observer>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import store from '@/store'
import { NORMAL_CUSTOMER_TYPE_VALUE } from '@/utils/defaultValue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'UploadWithholdingDocument',
  components: {
    ValidationObserver,
    ValidationProvider,
    BaseTextField: () => import('@/components/BaseTextField'),
    BaseFileInput: () => import('@/components/BaseFileInput')
  },
  data() {
    return {
      alert: false,
      isSubmitting: false,
      listWithholdingDocumentSrc: [],
      withholdingDocumentFile: [],
      selectOrderId: ''
    }
  },
  computed: {
    ...mapState('User', ['userProfile']),
    lineUid() {
      return this.userProfile?.uid || null
    },
    ...mapState('Order', ['order', 'orders']),
    mapOrders() {
      return this.orders
        .filter((order) => {
          // Filter orders with status 'pending', 'completed', or 'paid'
          const validStatuses = ['pending', 'completed', 'paid']
          if (!validStatuses.includes(order.status)) {
            return false
          }

          // Filter out orders that have withholding tax status other than 'pending' or 'completed'
          if (['pending', 'completed'].includes(order.withholdingTaxStatus)) {
            return false
          }

          // Filter out when customerTypeId is 'normal'
          if (order.customerTypeId === NORMAL_CUSTOMER_TYPE_VALUE) {
            return false
          }

          return true
        })
        .map((order) => ({ text: order.code, value: order._id }))
    },
    paymentMethod() {
      if (!this.order || !this.order.payments || this.order.payments.length === 0) {
        return null
      }

      // Find the last payment with type 'slip', 'creditCard', or 'qr'
      const validTypes = ['slip', 'creditCard', 'qr']
      const lastPayment = [...this.order.payments]
        .reverse()
        .find((payment) => validTypes.includes(payment.type))

      if (!lastPayment) {
        return null
      }

      // Map API payment types to component values
      const typeMapping = {
        slip: 'transfer',
        creditCard: 'byCreditCard',
        qr: 'qrCode'
      }

      return typeMapping[lastPayment.type] || null
    },
    hasFiles() {
      return this.listWithholdingDocumentSrc.length > 0
    }
  },
  async beforeRouteEnter(to, from, next) {
    try {
      store.dispatch('wait/start', 'app loading', { root: true })

      // Fetch orders that are eligible for withholding document upload
      const uploadWithholdingDocumentOrderStatus = ['pending', 'completed', 'paid']
      const orders = await store.dispatch('Order/fetchOrders', {
        status: uploadWithholdingDocumentOrderStatus.toString()
      })

      const { orderId } = to.params
      const findOrder = orders.find((order) => order._id === orderId)

      if (!findOrder) {
        const order = await store.dispatch('Order/fetchOrderById', orderId)
        if (order.status === 'deleted') {
          next({ name: 'NotFound' })
        } else {
          next()
        }
      } else {
        next()
      }

      store.dispatch('wait/end', 'app loading', { root: true })
    } catch {
      store.dispatch('wait/end', 'app loading', { root: true })
      next({ name: 'NotFound' })
    }
  },
  async mounted() {
    const { orderId } = this.$route.params
    this.selectOrderId = orderId

    if (this.selectOrderId) {
      await this.findOrderId()
    }
  },
  methods: {
    async findOrderId() {
      if (this.selectOrderId) {
        const order = await store.dispatch('Order/fetchOrderById', this.selectOrderId)
        return order
      }
      return null
    },
    async selectOrder(val) {
      this.selectOrderId = val
      if (val) {
        this.$router.push({ path: `/upload-withholding-document/${val}` })
        await this.findOrderId()
      }
    },
    formatNumber(value) {
      if (!value) return '0.00'
      return parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    isImageDocument(file) {
      return file && file.type && file.type.startsWith('image/')
    },
    uploadWithholdingDocumentFile(file) {
      if (!file || file.length === 0) {
        this.withholdingDocumentFile = []
        this.listWithholdingDocumentSrc = []
        return
      }

      file.forEach((item) => {
        if (item.type !== 'application/pdf' && item.type !== 'image/jpeg' && item.type !== 'image/png' && item.type !== 'image/jpg') {
          this.$swal({
            title: 'เกิดข้อผิดพลาด',
            text: 'กรุณาอัพโหลดไฟล์นามสกุล .jpg,png,pdf,jpeg เท่านั้น',
            icon: 'error',
            confirmButtonColor: '#01BB00',
            confirmButtonText: 'ตกลง'
          })
          throw new Error('Invalid file type for attachment')
        }
        if (item.size > 10 * 1024 * 1024) {
          this.$swal({
            title: 'เกิดข้อผิดพลาด',
            text: 'กรุณาอัพโหลดไฟล์ที่มีขนาดไม่เกิน 10 MB',
            icon: 'error',
            confirmButtonColor: '#01BB00',
            confirmButtonText: 'ตกลง'
          })
          throw new Error('File size exceeds limit')
        }
      })

      this.withholdingDocumentFile = file
      this.listWithholdingDocumentSrc = file.map((value) => ({
        file: value,
        url: URL.createObjectURL(value)
      }))
    },
    removeWithholdingDocumentFile(index) {
      if (Array.isArray(this.listWithholdingDocumentSrc) && this.listWithholdingDocumentSrc.length > index) {
        this.listWithholdingDocumentSrc.splice(index, 1)
      }
      if (Array.isArray(this.withholdingDocumentFile) && this.withholdingDocumentFile.length > index) {
        this.withholdingDocumentFile.splice(index, 1)
      }
    },
    async submit() {
      const canSubmit = await this.$refs.uploadWithholdingDocumentForm.validate()
      if (!canSubmit) {
        this.$utils.scrollToErrorField('uploadWithholdingDocumentForm', this)
        return
      }

      this.isSubmitting = true

      try {
        const bodyData = {
          type: 'attachment',
          totalImage: this.withholdingDocumentFile.length
        }

        const formData = new FormData()
        if (this.withholdingDocumentFile && this.withholdingDocumentFile.length > 0) {
          this.withholdingDocumentFile.forEach((file) => {
            formData.append('file', file)
          })

          formData.append('bodyData', JSON.stringify(bodyData))

          await this.updateOrderPayments({ id: this.selectOrderId, params: formData })

          this.alert = true

          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'click_tax_btn_juristic_person_confirm',
            category: 'user_action',
            label: 'tax_btn_juristic_person_confirm',
            value: 1,
            userId: this.lineUid
          })

          // Show success modal
          this.showSuccessModal()
        }
      } catch (error) {
        this.$swal({
          title: 'แนบเอกสารไม่สำเร็จ',
          html: 'เกิดความผิดพลาดระหว่างการแนบเอกสาร<br>กรุณาแนบเอกสารใหม่อีกครั้ง',
          icon: 'error'
        })
      } finally {
        this.isSubmitting = false
      }
    },
    showSuccessModal() {
      this.$swal({
        icon: 'success',
        title: 'แนบเอกสารเรียบร้อย',
        text: 'สามารถดาวน์โหลดใบเสร็จรับเงิน/ใบกำกับภาษี ได้ที่หน้ารายการสั่งซื้อ ภายใน 1-3 วันทำการ',
        confirmButtonText: 'ตกลง',
        allowOutsideClick: false
      }).then(() => {
        this.$router.push({ name: 'OrderList' })
      })
    },
    ...mapActions('Order', ['updateOrderPayments'])
  }
}
</script>

<style scoped>
</style>
